import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Pencil } from 'lucide-react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { BultoLogisticsForm } from './BultoLogisticsForm';
import { WeightUnitToggle } from './WeightUnitToggle';
import { selectHasFulfillmentPro } from '../../features/auth/authSelectors';
import {
  AWAITING_DISPATCH_STATUSES,
  EDITABLE_ITEM_STATUSES,
  itemHasLogistics,
} from '../../features/deliveryItems/deliveryItemsConstants';
import {
  fetchDeliveryItems,
  setDeliveryItemsCount,
} from '../../features/deliveryItems/deliveryItemsThunks';
import {
  clearDeliveryItems,
  setFilters,
  setPaginationRowsPerPage,
} from '../../features/deliveryItems/deliveryItemsSlice';
import { getOrderDetailRequest } from '../../features/orderDetails/orderDetailsApi';
import { formatWeight, useWeightUnit } from '../../features/deliveryItems/weightUnit';

// Same cap as the backend's MAX_DELIVERY_ITEMS, and the page size the modal
// asks for: every bulto of the order fits on one screen here, no pagination.
export const MAX_ITEMS = 100;

const DEFAULT_ROWS_PER_PAGE = 20;

/**
 * The bultos of one shipment, from the deliveries listing.
 *
 * Two things in one dialog, under the same rules as the "Bultos" tab of the
 * order detail:
 *
 * - **How many.** The total, added or removed from the end, while the shipment
 *   is still in a status where its bultos can change (`EDITABLE_ITEM_STATUSES`).
 * - **What each one carries.** Weight, dimensions and the products inside,
 *   only with the Fulfillment Pro addon and only while the order waits to be
 *   handed over (`AWAITING_DISPATCH_STATUSES`), which is when the detail lets
 *   a row be clicked. The form is the detail's own, `BultoLogisticsForm`.
 *
 * The bultos come from the shared `deliveryItems` slice, so a count change
 * and a saved bulto show up at once; the order's products are fetched here
 * because the listing row does not carry them.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} props.row - The listing row: `order_id`, `total_items`,
 *   `status` (the delivery's) and `reference_id`.
 */
export const ManageBultosModal = ({ isOpen, onClose, row }) => {
  const dispatch = useDispatch();
  const orderId = row?.order_id;
  const hasFulfillmentPro = useSelector(selectHasFulfillmentPro);
  const { data: items, count, loading, filters } = useSelector((state) => state.deliveryItems);
  // Kilos by default, grams on request. Display only: the backend keeps grams.
  const [weightUnit, setWeightUnit] = useWeightUnit();

  const [order, setOrder] = useState(null);
  const [orderError, setOrderError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [countValue, setCountValue] = useState('');
  const [savingCount, setSavingCount] = useState(false);
  const [savingLogistics, setSavingLogistics] = useState(false);
  const [error, setError] = useState(null);

  const hasSearchFilter = Boolean(filters?.search);

  // Load on open: the bultos (all of them, one page) and, when the logistics
  // half is shown, the order for its products. Starts from a clean slice so
  // the previous order's bultos never flash under this one's title.
  useEffect(() => {
    if (!isOpen || !orderId) return undefined;
    setEditing(null);
    setError(null);
    setOrder(null);
    setOrderError(null);
    dispatch(clearDeliveryItems());
    // A search left in the detail's bultos table is persisted and would hide
    // bultos here.
    if (hasSearchFilter) dispatch(setFilters({ search: '' }));
    dispatch(setPaginationRowsPerPage(MAX_ITEMS));
    dispatch(fetchDeliveryItems(orderId));

    let cancelled = false;
    if (hasFulfillmentPro) {
      getOrderDetailRequest(orderId)
        .then((response) => {
          if (!cancelled) setOrder(response.data?.order || null);
        })
        .catch(() => {
          if (!cancelled) setOrderError('No se pudieron cargar los productos de la orden.');
        });
    }
    return () => {
      cancelled = true;
    };
  }, [dispatch, isOpen, orderId, hasFulfillmentPro, hasSearchFilter]);

  // The count field follows the real total: the row's until the bultos load,
  // the slice's after that and after every change.
  const currentCount = count || row?.total_items || 0;
  useEffect(() => {
    if (isOpen) setCountValue(String(currentCount || ''));
  }, [isOpen, currentCount]);

  const deliveryStatus = row?.status;
  const canEditCount = EDITABLE_ITEM_STATUSES.includes(deliveryStatus);
  const isAwaitingDispatch = AWAITING_DISPATCH_STATUSES.includes(deliveryStatus);
  const orderProducts = order?.products || [];

  const ready = useMemo(() => items.filter(itemHasLogistics).length, [items]);

  const parsedCount = parseInt(countValue, 10);
  const countIsValid =
    parsedCount >= 1 && parsedCount <= MAX_ITEMS && parsedCount !== currentCount;

  const close = () => {
    if (savingCount || savingLogistics) return;
    dispatch(clearDeliveryItems());
    dispatch(setPaginationRowsPerPage(DEFAULT_ROWS_PER_PAGE));
    onClose();
  };

  const handleSetCount = async () => {
    if (!countIsValid || savingCount) return;
    setError(null);
    setSavingCount(true);
    try {
      await dispatch(setDeliveryItemsCount({ orderId, count: parsedCount })).unwrap();
    } catch (thunkError) {
      setError(thunkError);
    } finally {
      setSavingCount(false);
    }
  };

  const contentsOf = (item) =>
    (item.item_products || [])
      .map((assignment) => {
        const product = orderProducts.find((candidate) => candidate.id === assignment.order_product);
        return product ? `${product.product?.name} ×${assignment.prepared_quantity}` : null;
      })
      .filter(Boolean);

  const editTitle = !isAwaitingDispatch
    ? 'Las variables logísticas se editan mientras la orden espera despacho'
    : !order
      ? orderError || 'Cargando los productos de la orden...'
      : 'Editar variables logísticas';

  const title = row?.reference_id ? `Bultos · ${row.reference_id}` : 'Bultos';

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={title}
      size={hasFulfillmentPro ? 'lg' : 'sm'}
      testId="manage-bultos-modal"
      busy={savingCount || savingLogistics}
      error={error}
    >
      {editing ? (
        <>
          <p className="text-sm text-text-secondary text-center -mt-6 mb-6">
            Variables logísticas · Bulto {editing.item_index} de {editing.total_items}
          </p>
          <BultoLogisticsForm
            item={editing}
            items={items}
            orderProducts={orderProducts}
            deliveryId={order?.delivery?.id}
            onSaved={() => setEditing(null)}
            onCancel={() => setEditing(null)}
            onError={setError}
            onBusyChange={setSavingLogistics}
            unit={weightUnit}
            onUnitChange={setWeightUnit}
            cancelLabel="Volver"
          />
        </>
      ) : (
        <div className="flex flex-col gap-6">
          <div>
            <label
              htmlFor="manage-bultos-count"
              className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1.5"
            >
              Cantidad total de bultos
            </label>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <input
                  id="manage-bultos-count"
                  type="number"
                  min="1"
                  max={MAX_ITEMS}
                  value={countValue}
                  disabled={!canEditCount}
                  onChange={(e) => setCountValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSetCount()}
                  className="w-full bg-transparent border border-border rounded-xl pl-4 pr-16 py-2.5 text-sm focus:outline-none focus:border-ninja-green transition-colors disabled:opacity-60"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-text-muted">
                  bultos
                </span>
              </div>
              <Button
                variant="primary"
                pill
                onClick={handleSetCount}
                loading={savingCount}
                loadingLabel="Estableciendo..."
                disabled={!countIsValid || !canEditCount}
              >
                Establecer
              </Button>
            </div>
            <p className="text-xs text-text-muted mt-1.5">
              {canEditCount
                ? 'Los bultos extra se agregan o eliminan desde el final.'
                : 'En este estado la cantidad de bultos ya no se puede cambiar.'}
            </p>
          </div>

          {hasFulfillmentPro && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-text-muted">
                  Variables logísticas
                </span>
                <div className="flex items-center gap-4">
                  <WeightUnitToggle unit={weightUnit} onChange={setWeightUnit} />
                  {items.length > 0 && (
                    <span className="text-xs text-text-muted" data-testid="manage-bultos-ready">
                      Logística completa: {ready} / {items.length}
                    </span>
                  )}
                </div>
              </div>

              {loading && items.length === 0 ? (
                <p className="text-sm text-text-muted py-6 text-center">Cargando bultos...</p>
              ) : items.length === 0 ? (
                <p className="text-sm text-text-muted py-6 text-center">Esta orden no tiene bultos.</p>
              ) : (
                <div className="border border-border rounded-xl overflow-hidden">
                  <table className="w-full text-left border-collapse" data-testid="manage-bultos-table">
                    <thead>
                      <tr className="bg-ninja-gray/50 dark:bg-[#222]">
                        <th className="px-4 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider w-16">Bulto</th>
                        <th className="px-4 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">Productos</th>
                        <th className="px-4 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">Peso</th>
                        <th className="px-4 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">Dimensiones</th>
                        <th className="px-4 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider text-center">Estado</th>
                        <th className="px-2 py-2 w-12" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {items.map((item) => {
                        const itemReady = itemHasLogistics(item);
                        const contents = contentsOf(item);
                        return (
                          <tr key={item.id} data-testid={`manage-bulto-${item.item_index}`}>
                            <td className="px-4 py-2.5 font-bold text-ninja-dark dark:text-white">#{item.item_index}</td>
                            <td className="px-4 py-2.5">
                              {contents.length > 0 ? (
                                <div className="space-y-0.5">
                                  {contents.map((line) => (
                                    <p key={line} className="text-sm text-text-secondary">{line}</p>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-sm text-text-muted italic">Sin asignar</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-sm text-text-secondary whitespace-nowrap">
                              {formatWeight(item.weight_grams, weightUnit)}
                            </td>
                            <td className="px-4 py-2.5 text-sm text-text-secondary whitespace-nowrap">
                              {itemReady ? `${item.height_cm}×${item.width_cm}×${item.length_cm} cm` : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span
                                className={`px-2 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${itemReady
                                  ? 'bg-ninja-greenLight text-ninja-green'
                                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                                  }`}
                              >
                                {itemReady ? 'Listo' : 'Pendiente'}
                              </span>
                            </td>
                            <td className="px-2 py-2.5 text-center">
                              <Button
                                variant="subtle"
                                size="sm"
                                icon={Pencil}
                                iconOnly
                                disabled={!isAwaitingDispatch || !order}
                                title={editTitle}
                                aria-label={`Editar el bulto ${item.item_index}`}
                                onClick={() => setEditing(item)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {orderError && <p className="mt-2 text-sm text-danger">{orderError}</p>}
            </div>
          )}

          <div className="flex items-center justify-end">
            <Button variant="subtle" pill onClick={close} disabled={savingCount}>
              Cerrar
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
